import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TokenStorage } from '../../services/api';
import { useAuth } from '../../context/AuthContext';


const BASE_URL = process.env.REACT_APP_API_URL || 'https://code.mannaplag.mannaanalytics.com/api';


interface APIKey {
  id: string;
  name: string;
  key_prefix: string;
  is_active: boolean;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
  full_key?: string;  // only on creation
}

async function apiFetch(endpoint: string, options?: RequestInit) {
  const token = TokenStorage.getAccess();
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  });
  return { ok: res.ok, data: await res.json() };
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
const API_ACCESS_PLANS = ['Pro', 'Premium', 'Ultimate', 'Publisher'];

function getCurrentPlanName(user: any): string {
  return (
    user?.plan?.name ||
    user?.plan_name ||
    user?.subscription?.plan_name ||
    user?.subscription?.plan?.name ||
    user?.current_plan?.name ||
    user?.role ||
    ''
  );
}

export default function APIKeysPage() {

 const { user } = useAuth();
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<APIKey | null>(null);  
  const [copied, setCopied] = useState(false);
  const [msg, setMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [noAccess, setNoAccess] = useState(false);
  const [planName, setPlanName] = useState(String(getCurrentPlanName(user) || ''));
const [hasApiAccess, setHasApiAccess] = useState(false);
const [planChecked, setPlanChecked] = useState(false); // NEW — true only once subscription fetch resolves

  // Popup confirmation for disable/enable/revoke actions, mirroring the
  // "Suspend user" confirm dialog design used in the admin panel — replaces
  // the old window.confirm() browser alert.
  const [confirmAction, setConfirmAction] = useState<{ type: 'toggle' | 'revoke'; key: APIKey } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch real plan name from subscription API
useEffect(() => {
  apiFetch('/payments/my-subscription/')
     .then(({ data }: any) => {  
     const name = data?.plan_name || String(getCurrentPlanName(user) || '');
      setPlanName(name);
      setHasApiAccess(API_ACCESS_PLANS.map(p => p.toLowerCase()).includes(name.toLowerCase()));
    })
    .catch(() => {
      const name = String(getCurrentPlanName(user) || '');
      setPlanName(name);
      setHasApiAccess(API_ACCESS_PLANS.map(p => p.toLowerCase()).includes(name.toLowerCase()));
    })
    .finally(() => setPlanChecked(true)); // NEW
}, [user]);

  const showMsg = (text: string, type: 'success' | 'error' = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  };

  const loadKeys = async () => {
    setLoading(true);
    const { ok, data } = await apiFetch('/apikeys/');
    if (!ok) {
      if (data.upgrade_required) setNoAccess(true);
      else showMsg(data.error || 'Failed to load keys.', 'error');
    } else {
      setKeys(data);
    }
    setLoading(false);
  };

useEffect(() => {
  if (planChecked && hasApiAccess) loadKeys();
}, [planChecked, hasApiAccess]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    const { ok, data } = await apiFetch('/apikeys/', {
      method: 'POST',
      body: JSON.stringify({ name: newKeyName.trim() }),
    });
    if (!ok) { showMsg(data.error || 'Failed to create key.', 'error'); }
    else {
      setNewKey(data);
      setNewKeyName('');
      setShowCreate(false);
      loadKeys();
    }
    setCreating(false);
  };

  const handleToggle = async (key: APIKey) => {
    setActionLoading(true);
    const { ok, data } = await apiFetch(`/apikeys/${key.id}/`, {
      method: 'PATCH',
      body: JSON.stringify({ action: 'toggle' }),
    });
    if (!ok) showMsg(data.error, 'error');
    else { showMsg(data.message); loadKeys(); }
    setActionLoading(false);
    setConfirmAction(null);
  };

  const handleRevoke = async (key: APIKey) => {
    setActionLoading(true);
    const { ok, data } = await apiFetch(`/apikeys/${key.id}/`, { method: 'DELETE' });
    if (!ok) showMsg(data.error, 'error');
    else { showMsg('API key revoked.'); loadKeys(); }
    setActionLoading(false);
    setConfirmAction(null);
  };

  const runConfirmedAction = () => {
    if (!confirmAction) return;
    if (confirmAction.type === 'toggle') handleToggle(confirmAction.key);
    else handleRevoke(confirmAction.key);
  };

  const copyKey = () => {
    if (!newKey?.full_key) return;
    navigator.clipboard.writeText(newKey.full_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── No access — only show after plan is confirmed loaded ────
if (noAccess || (planChecked && !hasApiAccess)) return (
    <div className="api-keys-page-scope" style={{ flex: 1, overflowY: 'auto', background: '#f8fafc', fontFamily: "'Inter', sans-serif" }}>
      <style>{`.api-keys-page-scope, .api-keys-page-scope * { font-family: 'Inter', sans-serif; }`}</style>
      <div style={{ background: '#fff', borderBottom: '1px solid #e8edf4', padding: '12px 20px' }}>
        <p style={{ fontSize: 15, fontWeight: 900, color: '#111' }}>API Keys</p>
        <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>Programmatic access to Manna Plag</p>
      </div>
      <div style={{ padding: 40, maxWidth: 480 }}>
        <div style={{ background: '#fff', border: '1px solid #e8edf4', borderRadius: 16, padding: 40, textAlign: 'center' }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <svg width="24" height="24" fill="none" stroke="#d97706" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <p style={{ fontSize: 15, fontWeight: 900, color: '#111', marginBottom: 6 }}>API access requires Pro or Publisher plan</p>
          <p style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.6, marginBottom: 24 }}>
            Upgrade your plan to generate API keys and integrate Manna Plag directly into your journal workflow.
          </p>
          <Link to="/choose-plan" state={{ upgradeOnly: true }}
            style={{ display: 'inline-block', padding: '10px 24px', background: '#003c6c ', color: '#fff', borderRadius: 9, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
            Upgrade plan →
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="api-keys-page-scope" style={{ flex: 1, overflowY: 'auto', background: '#f8fafc', fontFamily: "'Inter', sans-serif" }}>
      <style>{`.api-keys-page-scope, .api-keys-page-scope * { font-family: 'Inter', sans-serif; }`}</style>

      {/* Page header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e8edf4', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: 15, fontWeight: 900, color: '#111', lineHeight: 1 }}>API Keys</p>
          <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>Programmatic access to Manna Plag — Publisher & Pro plans</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#003c6c ', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Generate new key
        </button>
      </div>

      <div style={{ padding: 20, maxWidth: 820 }}>

        {/* Message */}
        {msg && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, marginBottom: 14, border: `1px solid ${msg.type === 'success' ? '#bbf7d0' : '#fecaca'}`, background: msg.type === 'success' ? '#f0fdf4' : '#fef2f2', color: msg.type === 'success' ? '#16a34a' : '#dc2626' }}>
            <p style={{ fontSize: 12, fontWeight: 600 }}>{msg.text}</p>
          </div>
        )}

        {/* ── New key revealed modal ── */}
        {newKey && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
            <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
              <div style={{ background: '#003c6c ', padding: '20px 24px' }}>
                <p style={{ color: '#fff', fontSize: 15, fontWeight: 900 }}>Your new API key</p>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 3 }}>Copy this key now — it will never be shown again</p>
              </div>
              <div style={{ padding: 24 }}>
                {/* Warning */}
                <div style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#fffbeb', border: '1px solid #fde68a', marginBottom: 16 }}>
                  <svg width="16" height="16" fill="none" stroke="#d97706" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 1 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p style={{ fontSize: 12, color: '#92400e', fontWeight: 500, lineHeight: 1.5 }}>
                    This is the only time you'll see this key. Store it securely — we cannot recover it.
                  </p>
                </div>

                {/* Key display */}
                <div style={{ background: '#f8fafc', border: '1px solid #e8edf4', borderRadius: 9, padding: '12px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <code style={{ flex: 1, fontSize: 12, fontFamily: 'monospace', color: '#1f2937', wordBreak: 'break-all', lineHeight: 1.6 }}>
                    {newKey.full_key}
                  </code>
                  <button onClick={copyKey}
                    style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 6, border: '1px solid #e8edf4', background: copied ? '#f0fdf4' : '#fff', color: copied ? '#16a34a' : '#374151', fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap' }}>
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 0 }}>
                  <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
                    <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, marginBottom: 2 }}>KEY NAME</p>
                    <p style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{newKey.name}</p>
                  </div>
                  <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
                    <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, marginBottom: 2 }}>PREFIX</p>
                    <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', fontFamily: 'monospace' }}>{newKey.key_prefix}...</p>
                  </div>
                </div>
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid #f0f4f8' }}>
                <button onClick={() => { setNewKey(null); setCopied(false); }}
                  style={{ width: '100%', padding: '10px', background: '#003c6c ', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  I've saved my key — close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Create key modal ── */}
        {showCreate && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
            <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
              <div style={{ background: '#003c6c ', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ color: '#fff', fontSize: 15, fontWeight: 900 }}>Generate API key</p>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 3 }}>Give your key a descriptive name</p>
                </div>
                <button onClick={() => setShowCreate(false)}
                  style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              <div style={{ padding: 24 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
                  Key name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input type="text" value={newKeyName} onChange={e => setNewKeyName(e.target.value)}
                  placeholder="e.g. Production, Journal System, Testing"
                  onKeyDown={e => e.key === 'Enter' && handleCreate()}
                  autoFocus
                  style={{ width: '100%', border: '1.5px solid #e8edf4', borderRadius: 8, padding: '10px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 6 }}
                  onFocus={e => { (e.target as HTMLElement).style.borderColor = '#003c6c '; }}
                  onBlur={e => { (e.target as HTMLElement).style.borderColor = '#e8edf4'; }}
                />
                <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 20 }}>Choose a name that describes where this key will be used.</p>
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid #f0f4f8', display: 'flex', gap: 8 }}>
                <button onClick={() => setShowCreate(false)}
                  style={{ flex: 1, padding: '10px', border: '1px solid #e8edf4', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#6b7280', background: '#fff', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={handleCreate} disabled={creating || !newKeyName.trim()}
                  style={{ flex: 2, padding: '10px', background: '#003c6c ', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: creating || !newKeyName.trim() ? 'not-allowed' : 'pointer', opacity: creating || !newKeyName.trim() ? 0.6 : 1 }}>
                  {creating ? 'Generating...' : 'Generate key'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Keys table ── */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid #e8edf4', borderTopColor: '#003c6c ', animation: 'spin .8s linear infinite' }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : keys.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid #e8edf4', borderRadius: 12, padding: 60, textAlign: 'center' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="24" height="24" fill="none" stroke="#9ca3af" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#9ca3af', marginBottom: 4 }}>No API keys yet</p>
            <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 20 }}>Generate your first key to start integrating with Manna Plag.</p>
            <button onClick={() => setShowCreate(true)}
              style={{ padding: '9px 20px', background: '#003c6c ', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Generate first key
            </button>
          </div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e8edf4', borderRadius: 12, overflow: 'hidden' }}>

            {/* Column headers */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 120px 100px 90px', gap: 12, padding: '9px 18px', background: '#f8fafc', borderBottom: '1px solid #e8edf4' }}>
              {['Name', 'Key prefix', 'Created', 'Last used', 'Actions'].map((h, i) => (
                <span key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.07em', textAlign: i >= 4 ? 'center' : 'left' }}>{h}</span>
              ))}
            </div>

            {keys.map((key, i) => (
              <div key={key.id}
                style={{ display: 'grid', gridTemplateColumns: '1fr 140px 120px 100px 90px', gap: 12, padding: '13px 18px', borderBottom: i < keys.length - 1 ? '1px solid #f8fafc' : 'none', alignItems: 'center', opacity: key.is_active ? 1 : 0.55 }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f8fafc'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                {/* Name + status */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#1f2937' }}>{key.name}</p>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: key.is_active ? '#f0fdf4' : '#fef2f2', color: key.is_active ? '#16a34a' : '#dc2626' }}>
                      {key.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                </div>

                {/* Prefix */}
                <code style={{ fontSize: 12, fontFamily: 'monospace', color: '#6b7280', background: '#f8fafc', padding: '3px 8px', borderRadius: 5 }}>
                  {key.key_prefix}...
                </code>

                {/* Created */}
                <span style={{ fontSize: 11, color: '#6b7280' }}>{formatDate(key.created_at)}</span>

                {/* Last used */}
                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                  {key.last_used_at ? formatDate(key.last_used_at) : 'Never'}
                </span>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                  {/* Toggle */}
                  <button onClick={() => setConfirmAction({ type: 'toggle', key })}
                    title={key.is_active ? 'Disable key' : 'Enable key'}
                    style={{ width: 30, height: 30, borderRadius: 6, border: '1px solid #e8edf4', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: key.is_active ? '#d97706' : '#16a34a', transition: 'all .13s' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = key.is_active ? '#fffbeb' : '#f0fdf4'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff'; }}
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      {key.is_active
                        ? <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        : <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      }
                    </svg>
                  </button>

                  {/* Revoke */}
                  <button onClick={() => setConfirmAction({ type: 'revoke', key })}
                    title="Revoke key"
                    style={{ width: 30, height: 30, borderRadius: 6, border: '1px solid #e8edf4', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', transition: 'all .13s' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#fef2f2'; (e.currentTarget as HTMLElement).style.color = '#dc2626'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff'; (e.currentTarget as HTMLElement).style.color = '#dc2626'; }}
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Usage guide */}
        <div style={{ background: '#fff', border: '1px solid #e8edf4', borderRadius: 12, padding: 20, marginTop: 14 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: '#111', marginBottom: 12 }}>How to use your API key</p>
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px', fontFamily: 'monospace', fontSize: 12, color: '#374151', lineHeight: 1.8 }}>
            <p style={{ color: '#9ca3af', marginBottom: 4 }}># Add to your request headers</p>
            <p>Authorization: Api-Key MAPL_your_key_here</p>
            <p style={{ color: '#9ca3af', marginTop: 8, marginBottom: 4 }}># Example — check a document</p>
            <p>POST {process.env.REACT_APP_API_URL || 'https://code.mannaplag.mannaanalytics.com'}/submissions/upload/</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
            {[
              { label: 'Rate limit', value: 'Based on your plan quota' },
              { label: 'Authentication', value: 'Authorization: Api-Key' },
              { label: 'Max keys', value: '10 per account' },
              { label: 'Key format', value: 'MAPL_xxxxxxxxxxxxxxxx' },
            ].map(r => (
              <div key={r.label} style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
                <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, marginBottom: 2 }}>{r.label}</p>
                <p style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{r.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Disable / Revoke confirm dialog (replaces window.confirm) ── */}
      {confirmAction && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}
          onClick={() => !actionLoading && setConfirmAction(null)}
        >
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f0f4f8' }}>
              <p style={{ fontSize: 15, fontWeight: 900, color: '#111' }}>
                {confirmAction.type === 'revoke' ? 'Revoke key' : confirmAction.key.is_active ? 'Disable key' : 'Enable key'}
              </p>
              <button onClick={() => !actionLoading && setConfirmAction(null)}
                style={{ width: 26, height: 26, borderRadius: 6, border: 'none', background: '#f8fafc', color: '#9ca3af', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
            <div style={{ padding: '18px 20px' }}>
              <p style={{ fontSize: 13, color: '#6b7280', fontWeight: 500, lineHeight: 1.6 }}>
                {confirmAction.type === 'revoke'
                  ? `Are you sure you want to revoke "${confirmAction.key.name}"? This cannot be undone — any integration using this key will stop working immediately.`
                  : confirmAction.key.is_active
                    ? `Are you sure you want to disable "${confirmAction.key.name}"? Requests using this key will be rejected until it's re-enabled.`
                    : `Are you sure you want to enable "${confirmAction.key.name}"? It will immediately start accepting requests again.`}
              </p>
            </div>
            <div style={{ padding: '14px 20px', borderTop: '1px solid #f0f4f8', display: 'flex', gap: 8 }}>
              <button onClick={() => setConfirmAction(null)} disabled={actionLoading}
                style={{ flex: 1, padding: '10px', border: '1px solid #e8edf4', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#6b7280', background: '#fff', cursor: actionLoading ? 'not-allowed' : 'pointer' }}>
                Cancel
              </button>
              <button onClick={runConfirmedAction} disabled={actionLoading}
                style={{
                  flex: 1, padding: '10px', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#fff', cursor: actionLoading ? 'not-allowed' : 'pointer', opacity: actionLoading ? 0.7 : 1,
                  background: confirmAction.type === 'revoke' || confirmAction.key.is_active ? '#dc2626' : '#003c6c ',
                }}>
                {actionLoading
                  ? 'Please wait…'
                  : confirmAction.type === 'revoke' ? 'Revoke' : confirmAction.key.is_active ? 'Disable' : 'Enable'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
